"use client";

import { useT } from "../TranslatedText";

import { useEffect, useRef } from "react";
import moment from "moment-timezone";
import styles from "./styles/calendar.module.scss";
import classNames from "classnames";
import { CSSTransition } from "react-transition-group";
import { IReactiveEventContentItem } from "../../schoolfeed/SchoolFeedContentItem";
import { MonthCalendar } from "./MonthCalendar";
import {
  currentDateFormat,
  ICurrentRange,
  useCleverCalendarData,
} from "./utils";
import { ListCalendar } from "./ListCalendar";
import { ScrollArea } from "@radix-ui/themes";

const transitionClassNames = {
  enter: styles.transitionEnter,
  enterActive: styles.transitionEnterActive,
  enterDone: styles.transitionEnterDone,
  exit: styles.transitionExit,
  exitActive: styles.transitionExitActive,
  exitDone: styles.transitionExitDone,
};

export default function CleverCalendar(props: {
  events: IReactiveEventContentItem[];
  onRangeChange: (range: ICurrentRange) => void;
  aboveTheFold: boolean;
  expandedView?: boolean;
  loading?: boolean;
  handleDeleteEvent: (contentItem: ContentItem) => void;
  initialDate?: string;
}) {
  const { currentMonth, currentDate, nextMonth, prevMonth, setCurrentDate } =
    useCleverCalendarData(props.onRangeChange, props.initialDate);
  const t = useT();

  const transitionRefEvents = useRef(null);
  const transitionRefCalendar = useRef(null);
  const mobileMonthCalendarRef = useRef<HTMLDivElement>(null);
  const scrollAreaViewportRef = useRef<HTMLDivElement>(null);

  const setCurrentDateAndSnapMobileCalendarIntoView = (date: string) => {
    setCurrentDate(moment(date, currentDateFormat));

    const mobileCalendar = mobileMonthCalendarRef.current;
    const eventsContainer = document.getElementById("events-container");
    if (!mobileCalendar || !eventsContainer) return;
    if (window.getComputedStyle(mobileCalendar).display === "none") return;

    const calendarTop =
      mobileCalendar.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top: calendarTop, behavior: "smooth" });
  };

  useEffect(() => {
    const scroller = scrollAreaViewportRef.current;
    if (!scroller) return;

    const allEvents = Array.from(
      scroller.querySelectorAll<HTMLLIElement>("li[data-start-date]"),
    );
    if (!allEvents.length) return;

    const currentDateStr = currentDate.format("MMM-D-YYYY");
    let target: HTMLLIElement | undefined = allEvents.find(
      (ev) => ev.dataset.startDate === currentDateStr,
    );

    if (!target) {
      const targetTime = currentDate.valueOf();
      let closestDiff = Infinity;
      for (const ev of allEvents) {
        const evDate = moment(ev.dataset.startDate, "MMM-D-YYYY");
        const diff = Math.abs(evDate.valueOf() - targetTime);
        if (diff < closestDiff) {
          closestDiff = diff;
          target = ev;
        }
      }
    }

    if (!target) return;

    const eventRect = target.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    const offset = eventRect.top - scrollerRect.top + scroller.scrollTop;

    scroller.scroll({ top: Math.max(0, offset), behavior: "smooth" });
  }, [currentDate, props.aboveTheFold, props.events.length]);

  const contentContainerClassName = classNames({
    [styles.contentContainer]: true,
  });
  const contentTransitionClassName = classNames({
    [styles.contentTransition]: true,
    [styles.contentTransitionHeightConstrained]: !props.expandedView,
  });

  const listCalendar = (
    <ListCalendar
      currentMonth={currentMonth}
      currentDate={currentDate.format(currentDateFormat)}
      setCurrentDate={(date) => setCurrentDate(moment(date, currentDateFormat))}
      events={props.events}
      prevMonth={prevMonth}
      nextMonth={nextMonth}
      handleDeleteEvent={props.handleDeleteEvent}
      hideMonthNavigation
      hideLocation
      isLoading={props.loading}
    />
  );

  return (
    <div className={styles.container}>
      <div className={contentContainerClassName}>
        <div
          ref={mobileMonthCalendarRef}
          className={styles.mobileMonthCalendar}
        >
          <MonthCalendar
            currentMonth={currentMonth}
            currentDate={currentDate.format(currentDateFormat)}
            setCurrentDate={setCurrentDateAndSnapMobileCalendarIntoView}
            events={props.events}
            prevMonth={prevMonth}
            nextMonth={nextMonth}
          />
        </div>
        <CSSTransition
          nodeRef={transitionRefEvents}
          appear
          in={props.aboveTheFold}
          timeout={500}
          classNames={transitionClassNames}
        >
          <div
            ref={transitionRefEvents}
            className={contentTransitionClassName}
            id={"events-container"}
          >
            <div className={styles.scrollAreaWrapper}>
              <ScrollArea
                tabIndex={0}
                role="region"
                aria-label={t("Calendar events")}
                ref={scrollAreaViewportRef}
                scrollbars="vertical"
                size="2"
              >
                {listCalendar}
              </ScrollArea>
            </div>
          </div>
        </CSSTransition>
      </div>

      <div className={contentContainerClassName}>
        <CSSTransition
          nodeRef={transitionRefCalendar}
          appear
          in={props.aboveTheFold}
          timeout={500}
          classNames={transitionClassNames}
        >
          <div
            ref={transitionRefCalendar}
            className={`${contentTransitionClassName} ${styles.eventListContainer}`}
          >
            <MonthCalendar
              currentMonth={currentMonth}
              currentDate={currentDate.format(currentDateFormat)}
              setCurrentDate={(date) =>
                setCurrentDate(moment(date, currentDateFormat))
              }
              events={props.events}
              prevMonth={prevMonth}
              nextMonth={nextMonth}
            />
          </div>
        </CSSTransition>
      </div>
    </div>
  );
}
